import { useEffect, useRef, useState } from 'react';

import { collection, getDocs, query, where } from 'firebase/firestore';

import { firestore } from '../firebase';
import { EVENT_STATUSES } from '../types/Event';
import { LEAGUE_MEMBER_REQUEST_STATUSES } from '../types/LeagueMemberRequest';
import { MEMBERSHIP_STATUSES } from '../types/Membership';
import { SEASON_STATUSES } from '../types/Season';
import { TEAM_STATUSES } from '../types/Team';

interface UseLeagueDeletionImpactProps {
  orgId?: string;
  leagueId?: string;
}

interface DeletionCounts {
  seasons: number;
  events: number;
  teams: number;
  memberRequests: number;
  members: number;
}

const EMPTY_COUNTS: DeletionCounts = { seasons: 0, events: 0, teams: 0, memberRequests: 0, members: 0 };

/**
 * One-shot (non-realtime) queries to count league-associated data for the deletion impact summary.
 * Uses getDocs instead of listeners to avoid Firestore watch stream issues during cascading deletes.
 */
export default function useLeagueDeletionImpact(props: UseLeagueDeletionImpactProps) {
  const { orgId, leagueId } = props;
  const [counts, setCounts] = useState<DeletionCounts>(EMPTY_COUNTS);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedKeyRef = useRef('');

  const fetchKey = orgId && leagueId ? `${orgId}-${leagueId}` : '';

  useEffect(() => {
    if (!orgId || !leagueId) {
      setCounts(EMPTY_COUNTS);
      setIsLoading(false);
      fetchedKeyRef.current = '';
      return;
    }

    if (fetchedKeyRef.current === fetchKey) return;

    let cancelled = false;
    setIsLoading(true);

    const fetchCounts = async () => {
      try {
        const [seasonsSnap, eventsSnap, teamsSnap, requestsSnap, membersSnap] = await Promise.all([
          getDocs(query(
            collection(firestore, `org/${orgId}/season`),
            where('leagueId', '==', leagueId),
            where('status', 'in', [SEASON_STATUSES.ACTIVE, SEASON_STATUSES.ARCHIVED]),
          )),
          getDocs(query(
            collection(firestore, `org/${orgId}/event`),
            where('leagueId', '==', leagueId),
            where('status', 'in', [EVENT_STATUSES.ACTIVE, EVENT_STATUSES.ARCHIVED, EVENT_STATUSES.CANCELLED]),
          )),
          getDocs(query(
            collection(firestore, `org/${orgId}/team`),
            where('leagueId', '==', leagueId),
            where('status', 'in', [TEAM_STATUSES.ACTIVE, TEAM_STATUSES.ARCHIVED]),
          )),
          getDocs(query(
            collection(firestore, `org/${orgId}/league/${leagueId}/leagueMemberRequest`),
            where('status', 'in', [
              LEAGUE_MEMBER_REQUEST_STATUSES.PENDING,
              LEAGUE_MEMBER_REQUEST_STATUSES.APPROVED,
              LEAGUE_MEMBER_REQUEST_STATUSES.REJECTED,
              LEAGUE_MEMBER_REQUEST_STATUSES.CANCELLED,
            ]),
          )),
          getDocs(query(
            collection(firestore, `org/${orgId}/membership`),
            where('leagues', 'array-contains', leagueId),
            where('status', 'in', [
              MEMBERSHIP_STATUSES.ACTIVE,
              MEMBERSHIP_STATUSES.ACTIVE_UNCLAIMED,
              MEMBERSHIP_STATUSES.PENDING,
              MEMBERSHIP_STATUSES.INACTIVE,
              MEMBERSHIP_STATUSES.DEPRECATED_ACTIVE,
            ]),
          )),
        ]);

        if (!cancelled) {
          setCounts({
            seasons: seasonsSnap.size,
            events: eventsSnap.size,
            teams: teamsSnap.size,
            memberRequests: requestsSnap.size,
            members: membersSnap.size,
          });
          setIsLoading(false);
          fetchedKeyRef.current = fetchKey;
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching league deletion impact:', err);
          setIsLoading(false);
          fetchedKeyRef.current = fetchKey;
        }
      }
    };

    fetchCounts();
    return () => { cancelled = true; };
  }, [orgId, leagueId, fetchKey]);

  return { counts, isLoading };
}
